import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, ImagePlus, Loader2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const field = "w-full rounded-lg border border-[#D6D6D0] bg-white px-3 py-2.5 text-sm text-[#111] outline-none transition-colors focus:border-[#4CCC06]";

const Header = ({ biz }) => (
  <>
    <div className="flex flex-wrap items-center justify-between gap-4 bg-[#0A0A0A] px-6 py-5" data-testid="request-brand-header">
      <img src={`${API}/api/billing/brand/logo`} alt="Advanced Laser Restoration" className="h-11 w-auto" />
      <div className="sm:text-right"><p className="text-base font-extrabold text-white" data-testid="request-business-name">{biz.name}</p>{biz.phone && <p className="text-xs text-[#A0A0A0]">{biz.phone}</p>}</div>
    </div>
    <div className="h-1 bg-[#4CCC06]" />
  </>
);

const PhotoPicker = ({ files, setFiles }) => (
  <div>
    <p className="mb-1.5 text-xs font-bold text-[#555]">Photos of the area (optional, up to 5)</p>
    <div className="flex flex-wrap gap-2">
      {files.map((f, i) => (
        <div key={i} className="relative h-20 w-20 overflow-hidden rounded-lg border border-[#D6D6D0]" data-testid={`request-photo-${i}`}>
          <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded bg-black/70 p-0.5 text-white" data-testid={`request-photo-remove-${i}`}><X size={12} /></button>
        </div>
      ))}
      {files.length < 5 && <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#999] text-[11px] font-bold text-[#2f8f00]" data-testid="request-photo-add"><ImagePlus size={18} />Add photo<input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { setFiles([...files, ...e.target.files].slice(0, 5)); e.target.value = ""; }} data-testid="request-photo-input" /></label>}
    </div>
  </div>
);

export default function RequestForm() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const detailsOnly = params.get("mode") === "details";
  const embed = params.get("embed") === "1";
  const [biz, setBiz] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ first_name: "", last_name: "", company: "", email: "", phone: "", address: "", details: "", timeframe: "", website: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!embed) return undefined;
    const prev = [document.documentElement.style.background, document.body.style.background];
    document.documentElement.style.background = "transparent"; document.body.style.background = "transparent";
    return () => { [document.documentElement.style.background, document.body.style.background] = prev; };
  }, [embed]);
  useEffect(() => { fetch(`${API}/api/billing/intake/${token}`).then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail); setBiz(j.business); }).catch((e) => setErr(e.message || "This form is not available.")); }, [token]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("mode", detailsOnly ? "details" : "request");
      files.forEach((x) => fd.append("photos", x));
      const r = await fetch(`${API}/api/billing/intake/${token}`, { method: "POST", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : "Something went wrong. Please try again.");
      setDone(true);
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  const shell = (c) => <div className={`min-h-screen font-body text-[#111] ${embed ? "bg-transparent p-0" : "bg-[#F4F4F1] px-4 py-10"}`} data-testid="request-form-page"><div className="mx-auto max-w-xl">{c}</div></div>;
  if (!biz) return shell(err ? <p className="rounded-xl bg-white p-6 text-center" data-testid="request-form-error">{err}</p> : <Loader2 className="mx-auto animate-spin" />);
  return shell(
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
      <Header biz={biz} />
      {done ? (
        <div className="space-y-2 p-8 text-center" data-testid="request-form-done">
          <CheckCircle2 size={40} className="mx-auto text-[#2f8f00]" />
          <p className="text-xl font-extrabold">Thank you, {f.first_name}!</p>
          <p className="text-sm text-[#555]">{detailsOnly ? `Your details were sent to ${biz.name}.` : `${biz.name} received your request and will be in touch soon${biz.phone ? `. Need us sooner? Call ${biz.phone}` : ""}.`}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3 p-6" data-testid="request-form">
          <div><h1 className="text-2xl font-extrabold">{detailsOnly ? "Your contact details" : "Request a free quote"}</h1><p className="text-sm text-[#666]">{detailsOnly ? `So ${biz.name} can send you quotes and invoices.` : "Tell us what needs cleaning and we'll get back to you with a quote."}</p></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <input className={field} placeholder="First name *" value={f.first_name} onChange={set("first_name")} required data-testid="request-first-name-input" />
            <input className={field} placeholder="Last name" value={f.last_name} onChange={set("last_name")} data-testid="request-last-name-input" />
          </div>
          <input className={field} placeholder="Company (optional)" value={f.company} onChange={set("company")} data-testid="request-company-input" />
          <div className="grid gap-3 sm:grid-cols-2">
            <input className={field} type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="request-email-input" />
            <input className={field} type="tel" placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="request-phone-input" />
          </div>
          <input className={field} placeholder="Service address" value={f.address} onChange={set("address")} data-testid="request-address-input" />
          {!detailsOnly && <>
            <textarea className={field} rows={4} placeholder="What needs cleaning? * (e.g. graffiti on a brick wall, about 20 ft)" value={f.details} onChange={set("details")} required data-testid="request-details-input" />
            <input className={field} placeholder="Preferred date or timeframe (e.g. next 2 weeks)" value={f.timeframe} onChange={set("timeframe")} data-testid="request-timeframe-input" />
            <PhotoPicker files={files} setFiles={setFiles} />
          </>}
          <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
          {err && <p className="rounded-lg bg-[#FFF0F0] p-2.5 text-sm text-[#b00000]" data-testid="request-form-submit-error">{err}</p>}
          <button type="submit" disabled={busy} data-testid="request-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#4CCC06] py-3.5 text-sm font-extrabold text-black transition-opacity disabled:opacity-50">{busy && <Loader2 size={16} className="animate-spin" />}{detailsOnly ? "SEND MY DETAILS" : "SEND REQUEST"}</button>
          <p className="text-center text-[11px] text-[#888]">Email or phone required so we can reach you.</p>
        </form>
      )}
      <p className="border-t bg-[#F6F6F4] px-6 py-3 text-[11px] text-[#777]">{biz.name} is an independently owned and operated Advanced Laser Restoration franchise.</p>
    </div>
  );
}
